import { Popover, Tooltip, Typography } from '@mui/material'
import { useExportSettingsMutation } from 'api/export/export.api'
import moment from 'moment'
import { ReactElement, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Button } from 'ui/atoms'
import InfoBanner from 'ui/components/InfoBanner/InfoBanner'
import SelectButton from 'ui/components/SelectButton/SelectButton'
import { DatePickerPeriod } from 'ui/molecules/pickers/DatePicker/DatePickerPeriod'

type ExportSettingsControlProps = {
  activeSystem: System | null
}

const MAX_PERIOD_LENGTH_DAYS = 31

export default function ExportSettingsControl({ activeSystem }: ExportSettingsControlProps): ReactElement | null {
  const { t } = useTranslation()
  const { mutateAsync: exportSettings } = useExportSettingsMutation()
  const [popoverAnchorEl, setPopoverAnchorEl] = useState<HTMLElement | null>(null)
  const [startDate, setStartDate] = useState(moment().utc().startOf('month').toISOString() as ISODateTime)
  const [endDate, setEndDate] = useState(moment().startOf('hour').toISOString() as ISODateTime)
  const [periodLengthValidationError, setPeriodLengthValidationError] = useState(false)
  const [selectedSubtype, setSelectedSubtype] = useState<Subtype | undefined>()
  const regularOptimizationSubtypes = activeSystem?.subtypes?.regular || []

  const handlePopoverOpen = (event: React.MouseEvent<HTMLElement>) => {
    if (activeSystem?.id === undefined) {
      return
    }

    setPopoverAnchorEl(event.currentTarget)
  }

  const handlePopoverClose = () => {
    setPopoverAnchorEl(null)
    setSelectedSubtype(undefined)
  }

  const validationErrorMessage = periodLengthValidationError
    ? t(`The selected period cannot be longer than {{maxDays}} days`, { maxDays: MAX_PERIOD_LENGTH_DAYS })
    : ''

  return (
    <>
      {regularOptimizationSubtypes.length > 1 ? (
        <>
          <Button
            icon="fal fa-sync"
            primary
            noRadiusLeft
            onClick={handlePopoverOpen}
            tooltip={t('Pick time period to export settings to integrated external system')}
          >
            {t('Export of settings')}
          </Button>
          <SelectButton
            className="mr-2"
            icon="fas fa-sort-down"
            noRadiusRight
            menuItems={regularOptimizationSubtypes.map((subtype: Subtype) => ({
              label: subtype.subtype_display_name,
              onClick: (event: React.MouseEvent<HTMLElement>) => {
                setSelectedSubtype(subtype)
                handlePopoverOpen(event)
              },
            }))}
          />
        </>
      ) : (
        <Button
          icon="fal fa-sync"
          primary
          className="mr-2"
          onClick={handlePopoverOpen}
          tooltip={t('Pick time period to export settings to integrated external system')}
        >
          {t('Export of settings')}
        </Button>
      )}

      <Popover
        open={Boolean(popoverAnchorEl)}
        anchorEl={popoverAnchorEl}
        onClose={handlePopoverClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
      >
        <div className="p-5">
          <Typography variant="h6" sx={{ fontWeight: 'bold' }} display="inline" alignSelf="center">
            {selectedSubtype
              ? t('Export settings for {{subtype}}', { subtype: selectedSubtype.subtype_display_name })
              : t('Export settings')}
          </Typography>
          <InfoBanner
            text={t('Export settings from the selected time period to the integrated external system')}
            style="info"
            variant="text"
          />
          <DatePickerPeriod
            startDate={startDate}
            endDate={endDate}
            onSelect={(newStartDate, newEndDate) => {
              const periodLength = moment(newEndDate).diff(moment(newStartDate), 'days')
              setPeriodLengthValidationError(periodLength > MAX_PERIOD_LENGTH_DAYS)

              if (newStartDate) {
                setStartDate(newStartDate)
              }
              if (newEndDate) {
                setEndDate(newEndDate)
              }
            }}
          />
          <Button secondary className="ml-2" onClick={handlePopoverClose}>
            {t('Cancel')}
          </Button>
          <Tooltip title={validationErrorMessage} arrow placement="top">
            <div className="inline-block">
              <Button
                primary
                className="ml-2"
                disabled={activeSystem?.id === undefined || periodLengthValidationError}
                onClick={() => {
                  if (activeSystem?.id === undefined) {
                    return
                  }

                  exportSettings({
                    system_id: activeSystem.id,
                    start_time: startDate as ISODateTime,
                    end_time: endDate as ISODateTime,
                    subtypes: selectedSubtype ? [selectedSubtype.subtype] : undefined,
                  })
                  handlePopoverClose()
                }}
              >
                {t('Export settings')}
              </Button>
            </div>
          </Tooltip>
        </div>
      </Popover>
    </>
  )
}
